import Link from 'next/link'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {clsx} from 'clsx'
import {getValidSpacingOrNone} from '@/products/flexible-web-toolkit/styles'
import type {Block} from '@/services/kontent-ai/types'
import {KontentResponsivePicture} from '@/products/flexible-web-toolkit/components/responsive-picture'
import {getContainerClass} from '../helpers'

enum ImageWidth {
	Small = 'w-20',
	Medium = 'w-40',
	Large = 'w-80',
	Full = 'w-full',
}

export type ImageContentItem = IContentItem<{
	image: Elements.AssetsElement
	mobileImage: Elements.AssetsElement
	imageWidth: Elements.MultipleChoiceElement
	snippetSpacingSpacing: Elements.MultipleChoiceElement
	snippetContainerResize: Elements.MultipleChoiceElement
	imageLink: Elements.TextElement
	imageAlignment?: Elements.MultipleChoiceElement
	openInNewTab: Elements.MultipleChoiceElement
	removeHyperlink?: Elements.MultipleChoiceElement
	effectCls?: Elements.TextElement
}>

export const ImageBlock: Block<ImageContentItem> = ({block}) => {
	// The string replace is used to remove the top & bottom margin on mobile (without editing the highly reused spacing const)

	const spacingChoice = getValidSpacingOrNone(
		block.elements.snippetSpacingSpacing[0]?.codename || 'default'
	).replace('my', 'my-0 sm:my')
	const imageWidth = block.elements.imageWidth[0]?.codename

	const sizes: Record<string, string> = {
		small: ImageWidth.Small,
		medium: ImageWidth.Medium,
		large: ImageWidth.Large,
		full: ImageWidth.Full,
	}

	const imgSize =
		sizes[imageWidth as keyof typeof imageWidth] || ImageWidth.Medium

	const imageSizesPxMap: Record<ImageWidth, string> = {
		[ImageWidth.Small]: '80px',
		[ImageWidth.Medium]: '160px',
		[ImageWidth.Large]: '320px',
		[ImageWidth.Full]: '100vw',
	}
	const sizesHint = imageSizesPxMap[imgSize as ImageWidth]

	const containerSize = getContainerClass(
		block.elements.snippetContainerResize[0]?.codename ?? '',
		'container-narrow'
	)

	const getImageAlignment = (alignment: string | undefined): string => {
		switch (alignment) {
			case 'left':
				return 'justify-start'
			case 'center':
				return 'justify-center'
			case 'right':
				return 'justify-end'
			default:
				return 'justify-start'
		}
	}

	const justifyClass = getImageAlignment(
		(
			block.elements.imageAlignment as {codename?: string}[] | undefined
		)?.[0]?.codename
	)

	const mobileImageEl = block.elements.mobileImage[0] ?? null
	const desktopImage = block.elements.image[0] ?? mobileImageEl

	const imageClassName = clsx(
		imgSize,
		'h-auto',
		block.elements.effectCls as string
	)
	const imagePictureClassName = clsx('block', imgSize)

	return (
		<div
			className={clsx(
				justifyClass,
				'flex',
				'rounded-md',
				'sm:px-8',
				'sm:py-4',
				containerSize,
				spacingChoice
			)}
			data-kontent-element-codename="card_image"
		>
			{desktopImage ? (
				<>
					{block.elements.imageLink &&
					!block.elements.removeHyperlink ? (
						<Link
							className={clsx('block h-full', imgSize)}
							href={block.elements.imageLink || ''}
							rel="noreferrer"
							target={
								block.elements.openInNewTab[0]?.codename ===
								'yes'
									? '_blank'
									: '_self'
							}
						>
							<KontentResponsivePicture
								className={imageClassName}
								desktopAsset={desktopImage}
								mobileAsset={mobileImageEl}
								pictureClassName={imagePictureClassName}
								sizes={sizesHint}
							/>
						</Link>
					) : (
						<KontentResponsivePicture
							className={imageClassName}
							desktopAsset={desktopImage}
							mobileAsset={mobileImageEl}
							pictureClassName={imagePictureClassName}
							sizes={sizesHint}
						/>
					)}
				</>
			) : null}
		</div>
	)
}
